/**
 * korsha.js — the only custom script the KORSHA sections need.
 *
 * Behaviours CSS cannot express on its own:
 *   1. drawers — the mobile navigation, size guide, filter panel, quick add
 *   2. disclosure panels (footer columns, drawer sub-menus, nav dropdowns)
 *   3. the sticky header's measurements and its stuck state
 *   4. finishing touches: images fading in as they load, sections revealing
 *      as they scroll into view, the cart count settling when it changes
 *
 * All motion is CSS (transform and opacity); this file only toggles classes.
 * Every duration reads --korsha-motion, so Theme settings → Motion and a
 * device's reduced-motion preference switch it all off in one place.
 *
 * Do not import this file from another module. It is loaded once from
 * ml-custom-head with a versioned URL; a relative import resolves to a
 * different URL, runs init() a second time and double-binds every control.
 * Other modules open a drawer with showModal() and the class `korsha-drawer`;
 * the delegated handlers here take care of closing and the scroll lock.
 *
 * Everything else in the design — hover crossfades, the sticky header, the
 * 2-column grid, the plus/minus glyph — is CSS. Cart, search, account and
 * add-to-cart stay on Horizon's own components; this file does not touch them.
 *
 * Written as a module with no imports and no dependencies. It is defensive
 * about missing nodes so a merchant removing a section in the theme editor
 * can never throw.
 */

const MOBILE_BREAKPOINT = '(min-width: 750px)';

/* -------------------------------------------------------------------------
   Navigation drawer
   ---------------------------------------------------------------------- */

/**
 * Opens a KORSHA drawer as a modal dialog.
 *
 * <dialog>.showModal() is doing the heavy lifting: focus trap, inertness of
 * the page behind, Escape to close and the ::backdrop. That is why there is
 * no focus-management code here — adding it would fight the platform rather
 * than help it.
 *
 * @param {HTMLDialogElement} drawer
 */
function openDrawer(drawer) {
  if (!(drawer instanceof HTMLDialogElement) || drawer.open) return;
  drawer.showModal();
  lockScroll();
}

/**
 * Locks the page behind an open drawer. Horizon scrolls the page inside
 * .page-wrapper on desktop, not the document, so an overflow style on <html>
 * alone does not stop it; Horizon's own `scroll-lock` attribute covers both.
 */
function lockScroll() {
  document.documentElement.setAttribute('scroll-lock', '');
}

function releaseScroll() {
  if (document.querySelector('dialog.korsha-drawer[open], dialog:modal')) return;
  document.documentElement.removeAttribute('scroll-lock');
  document.documentElement.style.removeProperty('overflow');
}

// Other KORSHA modules (quick add) open drawers of their own.
window.KorshaDrawers = { lockScroll, releaseScroll };

let drawersBound = false;

/**
 * Wires every drawer trigger, close control and backdrop — the navigation
 * drawer, the size guide, the filter panel and quick add.
 *
 * Delegated on document rather than bound per element: the filter panel and
 * quick add replace their own contents after load, and a listener bound to a
 * node that has since been swapped out would silently stop working.
 */
function initDrawers() {
  if (drawersBound) return;
  drawersBound = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const trigger = target.closest('[data-korsha-drawer-open]');
    if (trigger) {
      const drawer = document.getElementById(trigger.getAttribute('data-korsha-drawer-open'));
      if (drawer instanceof HTMLDialogElement) {
        event.preventDefault();
        openDrawer(drawer);
      }
      return;
    }

    const closer = target.closest('[data-korsha-drawer-close]');
    if (closer) {
      const drawer = closer.closest('dialog');
      if (drawer?.open) {
        event.preventDefault();
        drawer.close();
      }
      return;
    }

    // A click on the backdrop lands on the dialog element itself, never on a
    // child, which is what makes this test reliable. Drawers keep an inner
    // wrapper that fills the panel so a click inside never reaches here.
    if (target instanceof HTMLDialogElement && target.matches('dialog.korsha-drawer') && target.open) {
      target.close();
    }
  });

  // `close` does not bubble, so listen in the capture phase. The lock is only
  // released once no other KORSHA drawer is still open on top.
  document.addEventListener(
    'close',
    (event) => {
      if (!(event.target instanceof HTMLDialogElement) || !event.target.matches('dialog.korsha-drawer')) return;
      releaseScroll();
    },
    true
  );
}

/* -------------------------------------------------------------------------
   Disclosures
   ---------------------------------------------------------------------- */

/**
 * Toggles a panel from its button, keeping aria-expanded and the panel's
 * hidden state in step.
 *
 * `data-korsha-disclosure="desktop-open"` marks a panel that is a collapsible
 * accordion on a phone but a plain always-open column on a wider screen —
 * the footer. Those re-evaluate on breakpoint change so a rotated tablet does
 * not end up with a column that is permanently hidden.
 */
function initDisclosures(root = document) {
  const buttons = Array.from(root.querySelectorAll('[data-korsha-disclosure]'));
  if (!buttons.length) return;

  const desktop = window.matchMedia(MOBILE_BREAKPOINT);

  const apply = (button, expanded) => {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    button.setAttribute('aria-expanded', String(expanded));
    if (panel) panel.hidden = !expanded;
  };

  const sync = () => {
    buttons.forEach((button) => {
      if (button.getAttribute('data-korsha-disclosure') !== 'desktop-open') return;
      // On desktop the control is inert (pointer-events: none in CSS) and the
      // panel is always open; on mobile it returns to being a real accordion.
      apply(button, desktop.matches ? true : button.dataset.korshaWasOpen === 'true');
    });
  };

  buttons.forEach((button) => {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    const startsOpen = button.getAttribute('aria-expanded') === 'true';
    button.dataset.korshaWasOpen = String(startsOpen);
    if (panel) panel.hidden = !startsOpen;

    button.addEventListener('click', (event) => {
      event.preventDefault();
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.dataset.korshaWasOpen = String(!expanded);
      apply(button, !expanded);
    });
  });

  sync();
  desktop.addEventListener('change', sync);
}

/* -------------------------------------------------------------------------
   Select labels
   ---------------------------------------------------------------------- */

/**
 * Keeps the visible label of a KORSHA select in step with the invisible
 * native control laid over it (see .korsha-select in korsha.css). Scripts
 * that set a select's value programmatically dispatch `change` so this runs.
 */
function initSelectLabels() {
  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('.korsha-select__control')) return;
    const label = select.parentElement?.querySelector('.korsha-select__value');
    if (label) label.textContent = select.selectedOptions[0]?.textContent?.split(' — ')[0]?.trim() || '';
  });
}

/* -------------------------------------------------------------------------
   Header metrics
   ---------------------------------------------------------------------- */

/**
 * Publishes --header-height and --header-group-height.
 *
 * Horizon's own header set these, and parts of the theme we have not replaced
 * still read them — base.css derives --sticky-header-offset from the first,
 * and the product media gallery uses that to pin its column. With the KORSHA
 * header in place nothing else writes them, so a calc() consuming an undefined
 * variable would invalidate the whole declaration. This keeps them accurate.
 */
function initHeaderMetrics() {
  const group = document.getElementById('header-group');
  const header = document.querySelector('.korsha-header');
  if (!group || !header) return;

  const publish = () => {
    const root = document.documentElement.style;
    root.setProperty('--header-height', `${Math.round(header.getBoundingClientRect().height)}px`);
    root.setProperty('--header-group-height', `${Math.round(group.getBoundingClientRect().height)}px`);
  };

  publish();

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(publish);
    observer.observe(group);
    observer.observe(header);
  } else {
    window.addEventListener('resize', publish);
  }
}

/* -------------------------------------------------------------------------
   Sticky header
   ---------------------------------------------------------------------- */

const DESKTOP_HEADER = window.matchMedia('(min-width: 990px)');
let stickyBound = false;
let stickyHide = 0;
let stickyTicking = false;

/**
 * The header group sticks at a negative offset (see korsha.css), so the
 * announcement bar and the desktop logo row scroll away and the nav row or
 * the phone bar stays. This measures that offset and the height that remains
 * on screen, and marks the header `is-stuck` once it is pinned — the class
 * only fades in the condensed wordmark and a hairline shadow.
 *
 * --korsha-header-stuck is what anything pinned beneath the header (the
 * product details column, the filter sidebar, anchor scrolling) offsets by.
 */
function initStickyHeader() {
  const root = document.documentElement.style;
  const measure = () => {
    const group = document.getElementById('header-group');
    const header = group?.querySelector('.korsha-header--sticky');
    if (!group || !header) {
      stickyHide = 0;
      root.setProperty('--korsha-header-hide', '0px');
      root.setProperty('--korsha-header-stuck', '0px');
      return;
    }
    const anchor = header.querySelector(DESKTOP_HEADER.matches ? '.korsha-header__navrow' : '.korsha-header__bar');
    const groupRect = group.getBoundingClientRect();
    stickyHide = anchor ? Math.max(0, Math.round(anchor.getBoundingClientRect().top - groupRect.top)) : 0;
    root.setProperty('--korsha-header-hide', `${stickyHide}px`);
    root.setProperty('--korsha-header-stuck', `${Math.round(groupRect.height - stickyHide)}px`);
    updateStuck();
  };

  measure();
  if (stickyBound) return;
  stickyBound = true;

  const onScroll = () => {
    if (stickyTicking) return;
    stickyTicking = true;
    window.requestAnimationFrame(updateStuck);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  document.querySelector('.page-wrapper')?.addEventListener('scroll', onScroll, { passive: true });

  const group = document.getElementById('header-group');
  if (group && 'ResizeObserver' in window) new ResizeObserver(measure).observe(group);
  DESKTOP_HEADER.addEventListener('change', measure);
  document.addEventListener('shopify:section:load', measure);
}

function updateStuck() {
  stickyTicking = false;
  const header = document.querySelector('.korsha-header--sticky');
  if (!header) return;
  const wrapper = document.querySelector('.page-wrapper');
  const top = Math.max(window.scrollY, wrapper ? wrapper.scrollTop : 0);
  header.classList.toggle('is-stuck', stickyHide > 0 ? top >= stickyHide - 1 : top > 0);
}

/* -------------------------------------------------------------------------
   Navigation dropdowns
   ---------------------------------------------------------------------- */

/**
 * Menu items with children are disclosures: the chevron button toggles the
 * panel and aria-expanded. Pointer users also get hover (pure CSS). Escape
 * closes and returns focus to the button; so does focus leaving the item.
 */
function initNavDropdowns(root = document) {
  root.querySelectorAll('[data-korsha-nav-item]').forEach((item) => {
    if (!(item instanceof HTMLElement) || item.dataset.korshaBound) return;
    item.dataset.korshaBound = 'true';
    const toggle = item.querySelector('.korsha-nav-item__toggle');
    if (!(toggle instanceof HTMLButtonElement)) return;

    const set = (open) => {
      item.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
    };

    toggle.addEventListener('click', () => {
      const open = !item.classList.contains('is-open');
      document.querySelectorAll('[data-korsha-nav-item].is-open').forEach((other) => {
        if (other !== item) {
          other.classList.remove('is-open');
          other.querySelector('.korsha-nav-item__toggle')?.setAttribute('aria-expanded', 'false');
        }
      });
      set(open);
    });

    item.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !item.classList.contains('is-open')) return;
      set(false);
      toggle.focus();
    });

    item.addEventListener('focusout', (event) => {
      if (!(event.relatedTarget instanceof Node) || !item.contains(event.relatedTarget)) set(false);
    });
  });
}

let dropdownDismissBound = false;
function bindDropdownDismiss() {
  if (dropdownDismissBound) return;
  dropdownDismissBound = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    document.querySelectorAll('[data-korsha-nav-item].is-open').forEach((item) => {
      if (target && item.contains(target)) return;
      item.classList.remove('is-open');
      item.querySelector('.korsha-nav-item__toggle')?.setAttribute('aria-expanded', 'false');
    });
  });
}

/* -------------------------------------------------------------------------
   Cart: overlay, scrim and count
   ---------------------------------------------------------------------- */

/**
 * Horizon's cart drawer is a sidebar on desktop that squeezes the page and
 * reopens itself on the next page load. KORSHA lays it over the page instead
 * (korsha-horizon.css). This closes it from the scrim and opts it out of the
 * reopen-on-navigation with Horizon's own `no-persist` attribute.
 */
let cartBound = false;
function initCartOverlay() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  drawer.setAttribute('no-persist', '');

  try {
    const key = 'theme-drawer-open';
    const others = (sessionStorage.getItem(key) || '').split(',').filter((id) => id && id !== 'cart-drawer');
    if (others.length) sessionStorage.setItem(key, others.join(','));
    else sessionStorage.removeItem(key);
  } catch {
    // Storage can be unavailable (private mode); nothing to clean up then.
  }

  if (cartBound) return;
  cartBound = true;
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-korsha-cart-scrim]')) return;
    const cart = document.getElementById('cart-drawer');
    if (cart && typeof cart.close === 'function') cart.close();
  });
}

/**
 * When Horizon's cart icon rewrites the count, it settles in rather than
 * switching (the animation is in korsha.css).
 */
function initCartCount(root = document) {
  root.querySelectorAll('.korsha-header__cart-count').forEach((count) => {
    if (!(count instanceof HTMLElement) || count.dataset.korshaBound) return;
    count.dataset.korshaBound = 'true';
    let last = count.textContent?.trim();
    let timer;
    new MutationObserver(() => {
      const now = count.textContent?.trim();
      if (now === last) return;
      last = now;
      count.classList.remove('is-updated');
      void count.offsetWidth;
      count.classList.add('is-updated');
      window.clearTimeout(timer);
      timer = window.setTimeout(() => count.classList.remove('is-updated'), 700);
    }).observe(count, { subtree: true, childList: true, characterData: true });
  });
}

/* -------------------------------------------------------------------------
   Images fade in as they load
   ---------------------------------------------------------------------- */

// Lazy images only: anything eager is on the first screen, where a fade would
// only delay the largest paint.
const FADE_IMAGES = '.korsha-media img[loading="lazy"], .korsha-gallery img[loading="lazy"]';
let fadeBound = false;

function markLoaded(img) {
  img.classList.add('is-loaded');
}

function initImageFade() {
  if (fadeBound) return;
  fadeBound = true;

  const sweep = (root = document) => {
    root.querySelectorAll?.(FADE_IMAGES).forEach((img) => {
      if (img instanceof HTMLImageElement && img.complete) markLoaded(img);
    });
  };

  const onLoad = (event) => {
    const img = event.target;
    if (img instanceof HTMLImageElement && img.matches(FADE_IMAGES)) markLoaded(img);
  };
  document.addEventListener('load', onLoad, true);
  // A broken image is shown (as the ivory ground) rather than left invisible.
  document.addEventListener('error', onLoad, true);

  sweep();
  // Cards added later — load more, quick add, recommendations — can arrive
  // already cached, which fires no load event in some browsers.
  new MutationObserver((records) => {
    for (const record of records) record.addedNodes.forEach((node) => node instanceof Element && sweep(node));
  }).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('load', () => sweep());

  document.documentElement.classList.add('korsha-img-fade');
}

/* -------------------------------------------------------------------------
   Reveal on scroll
   ---------------------------------------------------------------------- */

/**
 * Elements marked data-korsha-reveal fade up a few pixels as they enter the
 * viewport; data-korsha-reveal="stagger" does the same for each child in turn.
 *
 * Nothing on the first screen is ever hidden: those elements are marked
 * revealed before the hiding class is set, so there is no flash and no delay
 * to the largest paint. Off in the theme editor, when Theme settings turn it
 * off, and under reduced motion — in all of which the content simply shows.
 */
let revealObserver = null;

function initReveal(root = document) {
  const items = Array.from(root.querySelectorAll('[data-korsha-reveal]:not(.is-revealed)'));
  if (!items.length) return;

  const enabled =
    getComputedStyle(document.documentElement).getPropertyValue('--korsha-reveal').trim() === '1' &&
    !window.Shopify?.designMode &&
    'IntersectionObserver' in window;

  if (!enabled) {
    items.forEach((item) => item.classList.add('is-revealed'));
    return;
  }

  revealObserver ??= new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        revealObserver?.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px' }
  );

  const fold = window.innerHeight;
  items.forEach((item) => {
    if (item.getAttribute('data-korsha-reveal') === 'stagger') {
      Array.from(item.children).forEach((child, index) => {
        if (child instanceof HTMLElement) child.style.setProperty('--korsha-i', String(Math.min(index, 8)));
      });
    }
    if (item.getBoundingClientRect().top < fold) item.classList.add('is-revealed');
    else revealObserver?.observe(item);
  });

  document.documentElement.classList.add('korsha-reveal-on');
}

/* -------------------------------------------------------------------------
   Boot
   ---------------------------------------------------------------------- */

let selectLabelsBound = false;

function init(root = document) {
  initDrawers();
  if (!selectLabelsBound) {
    selectLabelsBound = true;
    initSelectLabels();
  }
  initDisclosures(root);
  initHeaderMetrics();
  initStickyHeader();
  initNavDropdowns(root);
  bindDropdownDismiss();
  initCartOverlay();
  initCartCount(root);
  initImageFade();
  initReveal(root);
}

init();

// The theme editor replaces a section's markup in place rather than reloading
// the page, so anything bound above is gone after an edit. Re-binding the
// replaced subtree keeps the drawer and accordions working while a merchant
// is customising.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});
